import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Deliberately NOT the shared services/api.js `api` instance — that one carries an
// Authorization-header interceptor and a 401 handler that redirects to a login page,
// neither of which makes sense for an anonymous visitor opening a WhatsApp-shared pay
// link with no session at all.
const publicApi = axios.create({ baseURL: API_URL, timeout: 15000, headers: { 'Content-Type': 'application/json' } });

export const publicPayAPI = {
  // `amount` is the optional advance figure carried on the link (?amt=) — omitted, the
  // server charges the live outstanding balance.
  getSummary: (token, amount) => publicApi.get(`/public/pay/${token}`, { params: amount ? { amt: amount } : {} }),
  createOrder: (token, amount) => publicApi.post(`/public/pay/${token}/create-order`, amount ? { amount } : {}),
  verify: (token, body) => publicApi.post(`/public/pay/${token}/verify`, body),
};

let razorpayScript = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  if (!razorpayScript) {
    razorpayScript = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = resolve;
      s.onerror = () => reject(new Error('Failed to load payment gateway'));
      document.body.appendChild(s);
    });
  }
  return razorpayScript;
}

// Mirrors utils/onlinePayment.js's provider branching (AblePay hosted-redirect vs
// Razorpay JS-modal), against the public/no-login endpoints instead of the authenticated
// customer-portal ones.
export async function startPublicPayment({ token, amount, merchantName, onSuccess, onFailure }) {
  const { data: orderRes } = await publicPayAPI.createOrder(token, amount);
  const order = orderRes.data;

  if (order.provider === 'ablepay') {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = order.actionUrl;
    Object.entries(order.fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    return; // browser is navigating away — nothing left to do here
  }

  await loadRazorpay();
  const rzp = new window.Razorpay({
    key: order.keyId,
    amount: Math.round(order.amount * 100),
    currency: order.currency,
    name: merchantName,
    description: 'Outstanding balance payment',
    order_id: order.orderId,
    prefill: order.prefill,
    handler: async (response) => {
      try {
        const { data: verifyRes } = await publicPayAPI.verify(token, {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        onSuccess?.({ receiptNumber: verifyRes.data?.receiptNumber });
      } catch (err) {
        onFailure?.(err.response?.data?.message || 'Payment succeeded but confirmation failed — contact support.');
      }
    },
    modal: { ondismiss: () => onFailure?.(null) }, // null = user just closed it, no toast needed
  });
  rzp.on('payment.failed', () => onFailure?.('Payment failed — please try again.'));
  rzp.open();
}
